import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateQuiz() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    duration_minutes: '30',
    total_marks: '100'
  });
  const [questions, setQuestions] = useState([
    {
      id: '1',
      question: '',
      options: ['', '', '', ''],
      correct_answer: '',
      marks: '10'
    }
  ]);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, getAuthHeader());
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    }
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: String(questions.length + 1),
        question: '',
        options: ['', '', '', ''],
        correct_answer: '',
        marks: '10'
      }
    ]);
  };

  const removeQuestion = (index) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateOption = (qIndex, optIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[optIndex] = value;
    setQuestions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/tests`,
        {
          ...formData,
          duration_minutes: parseInt(formData.duration_minutes),
          total_marks: parseInt(formData.total_marks),
          questions: questions.map((q) => ({
            id: q.id,
            question: q.question,
            options: q.options,
            correct_answer: q.correct_answer,
            marks: parseInt(q.marks)
          }))
        },
        getAuthHeader()
      );
      toast.success('Quiz created successfully!');
      navigate('/teacher/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quiz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">Create Quiz / Test</h1>
          <p className="text-lg text-slate-600">Design assessment for your students</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Quiz Details */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Quiz Details</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Quiz Title *</Label>
                <Input
                  id="title"
                  data-testid="quiz-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Chapter 5 Quiz - Algebra"
                  className="text-slate-900"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  data-testid="quiz-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What does this quiz cover?"
                  className="text-slate-900"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="course">Course *</Label>
                  <Select
                    value={formData.course_id}
                    onValueChange={(value) => setFormData({ ...formData, course_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="course-select" className="text-slate-900">
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id} className="text-slate-900">
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="duration">Duration (min) *</Label>
                  <Input
                    id="duration"
                    data-testid="duration-input"
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    className="text-slate-900"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="marks">Total Marks *</Label>
                  <Input
                    id="marks"
                    data-testid="total-marks-input"
                    type="number"
                    value={formData.total_marks}
                    onChange={(e) => setFormData({ ...formData, total_marks: e.target.value })}
                    className="text-slate-900"
                    required
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Questions</h2>
              <Button
                type="button"
                data-testid="add-question-btn"
                onClick={addQuestion}
                variant="outline"
                className="rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </div>

            {questions.map((question, qIndex) => (
              <Card key={qIndex} data-testid={`question-${qIndex}`} className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Question {qIndex + 1}</h3>
                  {questions.length > 1 && (
                    <Button
                      type="button"
                      data-testid={`remove-question-${qIndex}`}
                      onClick={() => removeQuestion(qIndex)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Question Text *</Label>
                    <Textarea
                      data-testid={`question-text-${qIndex}`}
                      value={question.question}
                      onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                      placeholder="Enter your question"
                      className="text-slate-900"
                      required
                    />
                  </div>
                  <div>
                    <Label>Options *</Label>
                    <div className="space-y-2">
                      {question.options.map((option, optIndex) => (
                        <Input
                          key={optIndex}
                          data-testid={`option-${qIndex}-${optIndex}`}
                          value={option}
                          onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                          placeholder={`Option ${optIndex + 1}`}
                          className="text-slate-900"
                          required
                        />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Correct Answer *</Label>
                      <Select
                        value={question.correct_answer}
                        onValueChange={(value) => updateQuestion(qIndex, 'correct_answer', value)}
                        required
                      >
                        <SelectTrigger data-testid={`correct-answer-${qIndex}`} className="text-slate-900">
                          <SelectValue placeholder="Select correct option" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {question.options.filter(opt => opt.trim() !== '').length > 0 ? (
                            question.options
                              .filter(opt => opt.trim() !== '')
                              .map((opt, idx) => (
                                <SelectItem key={idx} value={opt} className="text-slate-900">
                                  Option {question.options.indexOf(opt) + 1}: {opt}
                                </SelectItem>
                              ))
                          ) : (
                            <SelectItem value="no-options" disabled className="text-slate-500">
                              Please fill in options first
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Marks *</Label>
                      <Input
                        data-testid={`marks-${qIndex}`}
                        type="number"
                        value={question.marks}
                        onChange={(e) => updateQuestion(qIndex, 'marks', e.target.value)}
                        className="text-slate-900"
                        required
                      />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/teacher/dashboard')}
              className="rounded-full px-8"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-quiz-btn"
              disabled={loading}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 px-8"
            >
              {loading ? 'Creating...' : 'Create Quiz'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
